import { useNavegacao } from "@/lib/navegacao";
import { useState } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Megaphone, ExternalLink, Send } from "lucide-react";
import { PageHeader } from "@/components/layout/page-header";
import { PageSkeleton } from "@/components/data/page-skeleton";
import { Segmented } from "@/components/layout/segmented";
import { ProdutorShell } from "@/components/mercado/produtor-shell";
import { ChipReferencial, StatusChip } from "@/components/mercado/status-chip";
import { rotuloVariedade } from "@/components/mercado/bloco-oferta";
import { useAuth } from "@/lib/auth-context";
import { useToast } from "@/hooks/use-toast";
import { fmtDataBR, fmtDiaCurto, fmtKgBR } from "@/lib/formato-mercado";
import { labelPerfilComprador } from "@shared/schema";
import type { OfertaProdutorDTO, RespostaOfertaDTO } from "@shared/mercado-ofertas";
import {
  useAbrirDetalhes,
  useDispensarResposta,
  useEncerrarOferta,
  useFecharOferta,
  useOfertas,
  usePublicarOferta,
  useReenviarChamada,
  type FiltroOfertas,
} from "@/hooks/use-ofertas";

/**
 * OFERTAS — "quem quer minha carga?".
 *
 * Um card por oferta, e dentro dele UMA LINHA POR RESPOSTA com a ação do estado:
 * quem disse que tem interesse (dispensar / abrir detalhes), quem já tem os
 * detalhes (dispensar / fechar) e quem fechou (a venda no Cacho). Sem tabela:
 * no celular, no pátio, a decisão é por pessoa, não por coluna.
 *
 * ⚠️ Sem kg de pedido, sem saldo, sem valor estimado e sem PARCIAL (059).
 * Fechar com um comprador fecha a CARGA — quem quiser dividir publica duas
 * ofertas, que é o que faria no WhatsApp de qualquer forma. A venda nasce
 * rascunho, sem preço e sem kg: os dois entram na emissão, com o preço do dia.
 */
export default function MercadoOfertas() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const [filtro, setFiltro] = useState<FiltroOfertas>("abertas");
  const ativo = user?.orgTipo !== "COMPRADOR";
  const { data, isLoading } = useOfertas(filtro, ativo);

  return (
    <ProdutorShell>
      <PageHeader
        titulo="Ofertas"
        descricao="Cargas que você colocou na praça e quem disse que tem interesse. O preço é o da praça no dia da carga."
        empilharQuandoEstreito
        acoesSecundarias={
          <Segmented<FiltroOfertas>
            valor={filtro}
            onChange={setFiltro}
            prefixoTestId="seg-ofertas"
            opcoes={[
              { id: "abertas", label: "Em aberto" },
              { id: "fechadas", label: "Fechadas" },
              { id: "todas", label: "Todas" },
            ]}
          />
        }
        acao={
          <Button onClick={() => setLocation("/mercado/ofertas/nova")} data-testid="button-ofertar-carga">
            <Megaphone className="h-4 w-4 mr-1.5" />Ofertar carga
          </Button>
        }
      />

      {isLoading ? (
        <PageSkeleton variante="painel" />
      ) : !data || data.length === 0 ? (
        <section className="rounded-lg border border-dashed bg-card/60 flex flex-col items-center justify-center text-center gap-2 px-4 py-10" data-testid="ofertas-vazio">
          <Megaphone className="h-6 w-6 text-muted-foreground" />
          <p className="text-sm font-medium">
            {filtro === "fechadas" ? "Nenhuma oferta fechada ainda" : "Nenhuma oferta na praça"}
          </p>
          <p className="text-xs text-muted-foreground max-w-sm">
            Coloque uma carga na praça: a chamada vai anônima, os compradores respondem se têm interesse
            e você fecha com quem quiser — o fechamento vira uma venda rascunho no Cacho.
          </p>
          <Button variant="outline" size="sm" className="mt-1" onClick={() => setLocation("/mercado/ofertas/nova")}>
            Ofertar carga
          </Button>
        </section>
      ) : (
        <div className="space-y-4">
          {data.map(oferta => <CardOferta key={oferta.id} oferta={oferta} />)}
        </div>
      )}
    </ProdutorShell>
  );
}

function chipDoStatus(oferta: OfertaProdutorDTO) {
  if (oferta.status === "RASCUNHO") return <StatusChip kind="warn" testId={`chip-${oferta.id}`}>Rascunho</StatusChip>;
  if (oferta.status === "FECHADA") return <StatusChip kind="pago" testId={`chip-${oferta.id}`}>Fechada</StatusChip>;
  if (oferta.status === "ENCERRADA") return <StatusChip kind="aberta" testId={`chip-${oferta.id}`}>Encerrada</StatusChip>;
  // Quantos INTERESSADOS, não quantos kg: a resposta não tem quantidade.
  const querem = oferta.respostas.filter(r => r.status === "QUER" || r.status === "DETALHES_ABERTOS");
  return (
    <StatusChip kind="info" testId={`chip-${oferta.id}`}>
      {querem.length === 0 ? "na praça" : `${querem.length} ${querem.length === 1 ? "interessado" : "interessados"}`}
    </StatusChip>
  );
}

function CardOferta({ oferta }: { oferta: OfertaProdutorDTO }) {
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const [fechar, setFechar] = useState<RespostaOfertaDTO | null>(null);
  const publicar = usePublicarOferta();
  const encerrar = useEncerrarOferta();
  const reenviar = useReenviarChamada();

  return (
    <section className="rounded-lg border bg-card shadow-xs" data-testid={`card-oferta-${oferta.id}`}>
      <header className="border-b px-3 py-2.5 space-y-1">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <button
            type="button"
            className="text-sm font-semibold text-left rounded-md hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            onClick={() => setLocation(`/mercado/ofertas/${oferta.id}`)}
            data-testid={`abrir-oferta-${oferta.id}`}
          >
            {oferta.identificador} · {rotuloVariedade(oferta.variedade)}
          </button>
          <div className="flex items-center gap-2">
            {chipDoStatus(oferta)}
            {oferta.status !== "RASCUNHO" && (
              <span className="text-[11px] text-muted-foreground">anônima na praça</span>
            )}
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground">
          {oferta.publicadaEm
            ? <>na praça desde {fmtDiaCurto(oferta.publicadaEm.substring(0, 10))} · </>
            : <>rascunho de {fmtDataBR(oferta.criadoEm.substring(0, 10))} · </>}
          disponível {fmtDiaCurto(oferta.disponivelEm)}
        </p>
      </header>

      <div className="p-3 space-y-3">
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <p className="font-mono text-xl font-semibold tabular-nums">~{fmtKgBR(oferta.volumeKg)}</p>
            {oferta.variedade !== "NANICA" && (
              <p className="text-[11px] text-muted-foreground">
                cerca de {Math.round(oferta.pctPrimeira)}% de 1ª
              </p>
            )}
          </div>
          <div className="text-right space-y-0.5">
            {/* A cotação é REFERÊNCIA, com etiqueta — nunca o preço da oferta. */}
            {oferta.cotacao && (
              <p className="text-[11px] text-muted-foreground flex items-center justify-end gap-1.5 flex-wrap">
                praça {fmtDataBR(oferta.cotacao.data).substring(0, 5)}
                <ChipReferencial referencial={oferta.cotacao.referencial} />
              </p>
            )}
            <p className="text-[11px] text-muted-foreground">
              {oferta.alcance.compradores} alcançados · {oferta.alcance.abriram} abriram
            </p>
          </div>
        </div>

        {oferta.status === "RASCUNHO" ? (
          <div className="rounded-lg border border-dashed px-3 py-4 text-center space-y-2">
            <p className="text-xs text-muted-foreground">Este rascunho ainda não está na praça.</p>
            {/* Um passo só: sem preço na oferta, não há cotação vencida para
                confirmar nem praça muda a decisão de publicar. */}
            <Button
              size="sm"
              disabled={publicar.isPending}
              onClick={() => publicar.mutate(oferta.id, {
                // O painel de envio mora na tela da oferta agora — publicar
                // leva direto para lá, que é o passo seguinte de verdade.
                onSuccess: () => setLocation(`/mercado/ofertas/${oferta.id}`),
                onError: (e: any) => toast({ title: "Não foi possível colocar na praça", description: e?.message, variant: "destructive" }),
              })}
              data-testid={`button-publicar-${oferta.id}`}
            >
              <Megaphone className="h-4 w-4 mr-1.5" />Colocar na praça
            </Button>
          </div>
        ) : oferta.respostas.length === 0 ? (
          <p className="text-xs text-muted-foreground rounded-lg border border-dashed px-3 py-4 text-center">
            Ninguém respondeu ainda. Reenvie a chamada para quem compra de você.
          </p>
        ) : (
          <ul className="divide-y rounded-lg border" data-testid={`respostas-${oferta.id}`}>
            {oferta.respostas.map(r => (
              <LinhaResposta
                key={r.id}
                oferta={oferta}
                resposta={r}
                onFechar={() => setFechar(r)}
              />
            ))}
          </ul>
        )}

        {oferta.status === "NA_PRACA" && (
          <div className="flex items-center justify-between gap-2 flex-wrap border-t pt-3">
            <p className="text-xs text-muted-foreground">
              A chamada continua na praça até {fmtDiaCurto(oferta.disponivelEm)}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => { reenviar.mutate(oferta.id); setLocation(`/mercado/ofertas/${oferta.id}`); }}
                data-testid={`button-reenviar-${oferta.id}`}
              >
                <Send className="h-4 w-4 mr-1.5" />Reenviar chamada
              </Button>
              {oferta.status === "NA_PRACA" && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={encerrar.isPending}
                  onClick={() => {
                    if (!window.confirm("Encerrar esta oferta? Ela sai da praça e para de receber interesse.")) return;
                    encerrar.mutate(oferta.id, {
                      onError: () => toast({ title: "Não foi possível encerrar", variant: "destructive" }),
                    });
                  }}
                  data-testid={`button-encerrar-${oferta.id}`}
                >
                  Encerrar
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      {fechar && (
        <DialogFechar
          oferta={oferta}
          resposta={fechar}
          onFechar={() => setFechar(null)}
        />
      )}
    </section>
  );
}

/** Uma resposta: quem é, o que pediu e a ação do estado dela. */
function LinhaResposta({
  oferta,
  resposta,
  onFechar,
}: {
  oferta: OfertaProdutorDTO;
  resposta: RespostaOfertaDTO;
  onFechar: () => void;
}) {
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  // `abrir` aqui já é a mutation `useAbrirDetalhes` — a navegação vem com nome próprio.
  const { abrir: abrirVenda } = useNavegacao();
  const abrir = useAbrirDetalhes();
  const dispensar = useDispensarResposta();

  const quem = resposta.quem;
  const nome = quem.nome ?? quem.telefoneMascarado;

  return (
    <li className="px-3 py-2.5 space-y-2" data-testid={`resposta-${resposta.id}`}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">
            {nome}
            {quem.tipo === "NOVO" && <span className="text-xs text-muted-foreground font-normal"> · novo</span>}
            {quem.cidade && <span className="text-xs text-muted-foreground font-normal"> · {quem.cidade}</span>}
          </p>
          <p className="text-[11px] text-muted-foreground">
            {resposta.status === "FECHADO"
              ? <>fechou esta carga</>
              : resposta.respondidoEm
                ? <>tem interesse · {fmtDiaCurto(resposta.respondidoEm.substring(0, 10))}</>
                : <span className="opacity-70">abriu {resposta.abertoEm ? fmtDiaCurto(resposta.abertoEm.substring(0, 10)) : ""} · sem resposta</span>}
            {quem.produtoresNoRizoma > 0 && <> · {quem.produtoresNoRizoma} {quem.produtoresNoRizoma === 1 ? "produtor" : "produtores"} no Rizoma</>}
          </p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {quem.perfil && <StatusChip kind="aberta">{labelPerfilComprador(quem.perfil)}</StatusChip>}
          {resposta.status === "DETALHES_ABERTOS" && <StatusChip kind="vista">Detalhes abertos</StatusChip>}
          {resposta.status === "DISPENSADO" && <StatusChip kind="aberta">Dispensado</StatusChip>}
          {resposta.status === "FECHADO" && <StatusChip kind="pago">Fechado</StatusChip>}
        </div>
      </div>

      {resposta.status === "FECHADO" && resposta.vendaId && (
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <p className="text-[11px] text-muted-foreground">
            venda criada no Cacho{resposta.vendaNumero ? ` · ${resposta.vendaNumero}` : " (rascunho)"}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => abrirVenda(`/cacho/vendas/${resposta.vendaId}`)}
            data-testid={`button-abrir-venda-${resposta.id}`}
          >
            Abrir venda <ExternalLink className="h-3 w-3 ml-1.5" />
          </Button>
        </div>
      )}

      {(resposta.status === "QUER" || resposta.status === "DETALHES_ABERTOS") && (
        <div className="grid grid-cols-2 gap-2">
          <Button
            variant="outline"
            size="sm"
            className="min-h-10"
            disabled={dispensar.isPending}
            onClick={() => dispensar.mutate(
              { ofertaId: oferta.id, respostaId: resposta.id },
              { onError: () => toast({ title: "Não foi possível dispensar", variant: "destructive" }) },
            )}
            data-testid={`button-dispensar-${resposta.id}`}
          >
            Dispensar
          </Button>
          {resposta.status === "QUER" ? (
            <Button
              size="sm"
              className="min-h-10"
              disabled={abrir.isPending}
              onClick={() => abrir.mutate(
                { ofertaId: oferta.id, respostaId: resposta.id },
                { onError: () => toast({ title: "Não foi possível abrir os detalhes", variant: "destructive" }) },
              )}
              data-testid={`button-abrir-detalhes-${resposta.id}`}
            >
              Abrir detalhes
            </Button>
          ) : (
            <Button
              size="sm"
              className="min-h-10"
              onClick={onFechar}
              data-testid={`button-fechar-${resposta.id}`}
            >
              Fechar → venda
            </Button>
          )}
        </div>
      )}
    </li>
  );
}

/**
 * Fechar a carga com UM comprador.
 *
 * ⚠️ Não pergunta kg (059): fechar com alguém fecha a carga inteira. Quem
 * quiser dividir publica duas ofertas — inventar aqui um campo de quantidade
 * criaria um saldo que a venda depois teria de reconciliar, e o kg de verdade
 * só existe na balança.
 *
 * A venda nasce RASCUNHO **sem preço e sem kg**: os dois entram na emissão, com
 * o preço da praça no dia da carga. Prometer aqui o preço de hoje seria a
 * promessa que este ciclo inteiro existe para não fazer.
 */
function DialogFechar({
  oferta,
  resposta,
  onFechar,
}: {
  oferta: OfertaProdutorDTO;
  resposta: RespostaOfertaDTO;
  onFechar: () => void;
}) {
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const { abrir: abrirVenda } = useNavegacao();
  const fechar = useFecharOferta();

  return (
    <Dialog open onOpenChange={aberto => { if (!aberto) onFechar(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Fechar com {resposta.quem.nome ?? resposta.quem.telefoneMascarado}</DialogTitle>
          <DialogDescription>
            Isto fecha a carga e cria uma venda RASCUNHO no Cacho, sem preço e sem kg.
            Você lança os kg e o preço do dia na emissão do romaneio.
          </DialogDescription>
        </DialogHeader>

        <p className="text-xs text-muted-foreground">
          A oferta {oferta.identificador} sai da praça e para de receber interesse.
        </p>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" className="min-h-11" onClick={onFechar}>Cancelar</Button>
          <Button
            className="min-h-11"
            disabled={fechar.isPending}
            onClick={() => fechar.mutate(
              { ofertaId: oferta.id, respostaId: resposta.id },
              {
                onSuccess: (r) => {
                  onFechar();
                  toast({
                    title: "Carga fechada",
                    description: "A venda foi criada como rascunho no Cacho.",
                  });
                  abrirVenda(`/cacho/vendas/${r.vendaId}`);
                },
                onError: (e: any) => toast({
                  title: "Não foi possível fechar",
                  description: e?.message,
                  variant: "destructive",
                }),
              },
            )}
            data-testid="button-confirmar-fechar"
          >
            Fechar e criar venda
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
